import {
  buildExplicitPublicScopeAccess,
  type PublicScopeAccessSetting,
} from './wrPublicScope';
import { parseLegacyPublicScopeTargets } from './weeklyReportScopeTargets';

export type WeeklyReportPublicSettingRow = {
  attr_id?: unknown;
  option_id?: unknown;
  optionId?: unknown;
  id?: unknown;
  department_id?: unknown;
  team_id?: unknown;
  scope_type?: string | null;
  department_ids?: unknown;
  team_ids?: unknown;
  private_flg?: unknown;
  public_department?: unknown;
  public_team?: unknown;
  public?: Omit<WeeklyReportPublicSettingRow, 'public' | 'publish_scope' | 'publishScope'> | null;
  publish_scope?: Omit<WeeklyReportPublicSettingRow, 'public' | 'publish_scope' | 'publishScope'> | null;
  publishScope?: Omit<WeeklyReportPublicSettingRow, 'public' | 'publish_scope' | 'publishScope'> | null;
};

export type PublicScopeDisplay = {
  summaryText: string;
  tooltipText: string;
};

type OrganizationRow = Record<string, unknown>;

const normalizeId = (raw: unknown): string | null => {
  const source = String(raw ?? '').trim();
  if (!/^\d+$/.test(source)) return null;
  const value = Number(source);
  return Number.isSafeInteger(value) && value > 0 ? String(value) : null;
};

const unique = (values: string[]): string[] =>
  Array.from(new Set(values.map((value) => String(value ?? '').trim()).filter(Boolean)));

const splitLegacyTargets = (raw: unknown): string[] =>
  unique(parseLegacyPublicScopeTargets(raw));

const readOrganizationId = (
  row: OrganizationRow,
  keys: string[]
): string | null => {
  for (const key of keys) {
    const id = normalizeId(row?.[key]);
    if (id) return id;
  }
  return null;
};

const readOrganizationLabel = (row: OrganizationRow, fallback: string): string => {
  const label = String(
    row?.label ??
    row?.name ??
    row?.team_label ??
    row?.teamLabel ??
    row?.team_name ??
    row?.teamName ??
    row?.department_label ??
    row?.departmentLabel ??
    row?.department_name ??
    row?.departmentName ??
    row?.value ??
    row?.text ??
    fallback
  ).trim();
  return label || fallback;
};

const buildLabelMap = (
  rows: OrganizationRow[] | null | undefined,
  idKeys: string[]
): Record<string, string> => {
  const result: Record<string, string> = {};
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const id = readOrganizationId(row, idKeys);
    if (!id) return;
    result[id] = readOrganizationLabel(row, id);
  });
  return result;
};

const readScopePayload = (
  row: WeeklyReportPublicSettingRow
): WeeklyReportPublicSettingRow =>
  row.public ?? row.publish_scope ?? row.publishScope ?? row;

const resolveLegacyAccess = (
  row: WeeklyReportPublicSettingRow
): PublicScopeAccessSetting => {
  const scope = readScopePayload(row);
  const privateFlg = Number(scope.private_flg);
  const sourceDepartmentId = normalizeId(row.department_id);
  const legacyDepartments = splitLegacyTargets(scope.public_department);
  const legacyTeams = splitLegacyTargets(scope.public_team);

  if (privateFlg === 0) {
    return { visibility: 'public', departments: [], teams: [] };
  }
  if (legacyTeams.length > 0) {
    return { visibility: 'team', departments: [], teams: legacyTeams };
  }
  if (privateFlg === 2) {
    return {
      visibility: 'dept',
      departments:
        legacyDepartments.length > 0
          ? legacyDepartments
          : sourceDepartmentId
            ? [sourceDepartmentId]
            : [],
      teams: [],
    };
  }
  return { visibility: 'private', departments: [], teams: [] };
};

export const resolveWeeklyReportPublicScopeAccess = (
  row: WeeklyReportPublicSettingRow
): PublicScopeAccessSetting => {
  const explicit = buildExplicitPublicScopeAccess(row);
  if (!explicit) return resolveLegacyAccess(row);

  const scope = readScopePayload(row);
  const scopeType = String(scope.scope_type ?? '').trim().toLowerCase();
  // An unknown owner must not be replaced by stale legacy recipients.
  if (scopeType === 'department') return explicit;
  if (scopeType === 'teams' && explicit.teams.length === 0) {
    const legacyTeams = splitLegacyTargets(scope.public_team);
    if (legacyTeams.length > 0) {
      return { ...explicit, teams: legacyTeams };
    }
  }
  if (
    (scopeType === 'department' || scopeType === 'departments') &&
    explicit.departments.length === 0
  ) {
    const legacyDepartments = splitLegacyTargets(scope.public_department);
    if (legacyDepartments.length > 0) {
      return { ...explicit, departments: legacyDepartments };
    }
    const sourceDepartmentId = normalizeId(row.department_id);
    if (scopeType === 'department' && sourceDepartmentId) {
      return { ...explicit, departments: [sourceDepartmentId] };
    }
  }
  return explicit;
};

const mergeScopeAccess = (
  current: PublicScopeAccessSetting,
  candidate: PublicScopeAccessSetting
): PublicScopeAccessSetting => {
  if (current.visibility === 'public' || candidate.visibility === 'public') {
    return { visibility: 'public', departments: [], teams: [] };
  }

  const priority: Record<PublicScopeAccessSetting['visibility'], number> = {
    private: 0,
    team: 10,
    dept: 50,
    public: 100,
  };
  if (current.visibility !== candidate.visibility) {
    return priority[current.visibility] >= priority[candidate.visibility]
      ? current
      : candidate;
  }
  if (current.visibility === 'dept') {
    return {
      visibility: 'dept',
      departments: unique([...current.departments, ...candidate.departments]),
      teams: [],
    };
  }
  if (current.visibility === 'team') {
    return {
      visibility: 'team',
      departments: [],
      teams: unique([...current.teams, ...candidate.teams]),
    };
  }
  return current;
};

const parseOptionIds = (raw: unknown): string[] => {
  const direct = normalizeId(raw);
  if (direct) return [direct];
  return unique(
    String(raw ?? '')
      .match(/\d+/g)
      ?.map((value) => normalizeId(value))
      .filter((value): value is string => value != null) ?? []
  );
};

export const summarizePublicScopeTargets = (
  labels: string[],
  maxVisible = 3
): { summary: string; full: string } => {
  const normalized = unique(labels);
  const safeLimit = Math.max(1, Math.floor(maxVisible));
  return {
    summary:
      normalized.length > safeLimit
        ? `${normalized.slice(0, safeLimit).join('、')}…`
        : normalized.join('、'),
    full: normalized.join('、'),
  };
};

export const formatWeeklyReportPublicScope = (
  setting: PublicScopeAccessSetting,
  options: {
    departmentLabels?: Record<string, string>;
    teamLabels?: Record<string, string>;
    maxVisible?: number;
    departmentScopeMode?: 'department' | 'departments';
  } = {}
): PublicScopeDisplay => {
  if (setting.visibility === 'public') {
    return {
      summaryText: '公開',
      tooltipText: '公開範囲：すべてのユーザー',
    };
  }
  if (setting.visibility === 'private') {
    return {
      summaryText: '課内公開',
      tooltipText: '公開範囲：週報作成者の課のみ',
    };
  }

  const isTeamScope = setting.visibility === 'team';
  const rawTargets = isTeamScope ? setting.teams : setting.departments;
  const labelMap = isTeamScope
    ? options.teamLabels ?? {}
    : options.departmentLabels ?? {};
  const labels = unique(
    (rawTargets ?? []).map((target) => labelMap[String(target)] ?? String(target))
  );
  const { summary, full } = summarizePublicScopeTargets(
    labels,
    options.maxVisible ?? 3
  );
  const scopeLabel = isTeamScope
    ? 'チーム内公開'
    : options.departmentScopeMode === 'department'
      ? '課内公開'
      : '指定課に公開';
  const targetSummary = summary || '未設定';
  const targetFull = full || '未設定';

  return {
    summaryText: `${scopeLabel}：${targetSummary}`,
    tooltipText: `公開範囲：${scopeLabel}（${targetFull}）`,
  };
};

export const buildWeeklyReportPublicScopeDisplayMap = (args: {
  rows: WeeklyReportPublicSettingRow[] | null | undefined;
  departments?: OrganizationRow[] | null;
  teams?: OrganizationRow[] | null;
  maxVisible?: number;
}): Record<string, PublicScopeDisplay> => {
  const departmentLabels = buildLabelMap(args.departments, [
    'department_id',
    'departmentId',
    'id',
    'value',
  ]);
  const teamLabels = buildLabelMap(args.teams, ['team_id', 'teamId', 'id', 'value']);
  const accessByOptionId: Record<string, PublicScopeAccessSetting> = {};
  const departmentScopeModeByOptionId: Record<
    string,
    'department' | 'departments' | undefined
  > = {};

  (Array.isArray(args.rows) ? args.rows : []).forEach((row) => {
    const access = resolveWeeklyReportPublicScopeAccess(row);
    const scopeType = String(readScopePayload(row).scope_type ?? '')
      .trim()
      .toLowerCase();
    const candidateDepartmentScopeMode =
      scopeType === 'department'
        ? 'department'
        : scopeType === 'departments'
          ? 'departments'
          : access.visibility === 'dept'
            ? 'departments'
            : undefined;
    parseOptionIds(row.option_id ?? row.optionId ?? row.id).forEach((optionId) => {
      const current = accessByOptionId[optionId];
      if (!current) {
        accessByOptionId[optionId] = access;
        departmentScopeModeByOptionId[optionId] = candidateDepartmentScopeMode;
        return;
      }

      const merged = mergeScopeAccess(current, access);
      const currentMode = departmentScopeModeByOptionId[optionId];
      accessByOptionId[optionId] = merged;
      if (merged.visibility !== 'dept') {
        departmentScopeModeByOptionId[optionId] = undefined;
      } else if (current.visibility !== access.visibility) {
        departmentScopeModeByOptionId[optionId] =
          merged.visibility === access.visibility
            ? candidateDepartmentScopeMode
            : currentMode;
      } else {
        departmentScopeModeByOptionId[optionId] =
          currentMode === 'departments' || candidateDepartmentScopeMode === 'departments'
            ? 'departments'
            : 'department';
      }
    });
  });

  const result: Record<string, PublicScopeDisplay> = {};
  Object.entries(accessByOptionId).forEach(([optionId, setting]) => {
    result[optionId] = formatWeeklyReportPublicScope(setting, {
      departmentLabels,
      teamLabels,
      maxVisible: args.maxVisible ?? 3,
      departmentScopeMode: departmentScopeModeByOptionId[optionId],
    });
  });
  return result;
};

export const DEFAULT_PUBLIC_SCOPE_DISPLAY: PublicScopeDisplay = {
  summaryText: '公開',
  tooltipText: '公開範囲：すべてのユーザー',
};
